import type { ListBuilderColumn } from '../../types';
import { SENSOR_DEVICE_STATUS_VALUE_MAPPING } from './shared';

/** Device id (link to detail) with the device's friendly name as subtext. */
export const createDeviceColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-id',
	headers: [{ id: 'sensor-device-id-header', text: 'Device' }],
	fields: [
		{
			id: 'device-id-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'deviceId', objectTable: 'sensor_device' },
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
			link: { type: 'object', enabled: true },
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			id: 'device-name-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'name', objectTable: 'sensor_device' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
	],
	sortable: true,
});

/** Merchandiser the device is attached to (or "Unattached"). */
export const createDeviceMerchandiserColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-merchandiser',
	headers: [{ id: 'sensor-device-merchandiser-header', text: 'Merchandiser' }],
	fields: [
		{
			id: 'merchandiser-label-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'merchandiserLabel', objectTable: 'sensor_device' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	minWidth: 180,
});

/** Status badge. */
export const createDeviceStatusColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-status',
	headers: [{ id: 'sensor-device-status-header', text: 'Status' }],
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'status', objectTable: 'sensor_device' },
			},
			styleConfig: {
				size: 'sm',
				valueMapping: [...SENSOR_DEVICE_STATUS_VALUE_MAPPING],
			},
			displayStyle: 'badge',
		},
	],
	sortable: true,
	width: 130,
});

/** Sensor coverage — assigned-of-total (e.g. "3 / 4"); orphans when assigned < total. */
export const createDeviceCoverageColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-coverage',
	headers: [{ id: 'sensor-device-coverage-header', text: 'Sensors' }],
	fields: [
		{
			id: 'coverage-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'coverageLabel', objectTable: 'sensor_device' },
				defaultValue: '0 / 0',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 110,
});

/** Battery level (volts, derived from the device's millivolt reading). */
export const createDeviceBatteryColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-battery',
	headers: [{ id: 'sensor-device-battery-header', text: 'Battery' }],
	fields: [
		{
			id: 'battery-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'batteryLabel', objectTable: 'sensor_device' },
				defaultValue: '—',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 100,
});

/** Last time the device reported in. */
export const createDeviceLastSeenColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-last-seen',
	headers: [{ id: 'sensor-device-last-seen-header', text: 'Last Seen' }],
	fields: [
		{
			id: 'last-seen-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastSeenLabel', objectTable: 'sensor_device' },
				defaultValue: 'Never',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
	],
	sortable: true,
	minWidth: 150,
});

export const createSensorDeviceColumns = (): Array<ListBuilderColumn> => [
	createDeviceColumn(),
	createDeviceMerchandiserColumn(),
	createDeviceStatusColumn(),
	createDeviceCoverageColumn(),
	createDeviceBatteryColumn(),
	createDeviceLastSeenColumn(),
];
